import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Card } from '../../ui/card';
import { Plus, Edit, Trash2, Upload, Image as ImageIcon } from 'lucide-react';
import { API } from '../../../lib/api';

const INITIAL_FORM = { title: '', subtitle: '', image: '' };

const SuccessCasesTab = ({ successCases, getAuthHeaders, onRefresh }) => {
  const [showForm, setShowForm] = useState(false);
  const [editingCase, setEditingCase] = useState(null);
  const [form, setForm] = useState(INITIAL_FORM);
  const [saving, setSaving] = useState(false);

  const resetForm = () => {
    setForm(INITIAL_FORM);
    setEditingCase(null);
    setShowForm(false);
  };

  const openEdit = (successCase) => {
    setForm({
      title: successCase.title,
      subtitle: successCase.subtitle,
      image: successCase.image,
    });
    setEditingCase(successCase);
    setShowForm(true);
  };

  const handleImageChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      setForm((current) => ({ ...current, image: reader.result }));
    };
    reader.readAsDataURL(file);
  };

  const handleSave = async (event) => {
    event.preventDefault();
    if (!form.image) {
      toast.error('Adicione uma imagem para o case');
      return;
    }

    setSaving(true);
    try {
      if (editingCase) {
        await axios.put(`${API}/success-cases/${editingCase.id}`, form, getAuthHeaders());
        toast.success('Case atualizado!');
      } else {
        await axios.post(`${API}/success-cases`, form, getAuthHeaders());
        toast.success('Case criado!');
      }
      resetForm();
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar case');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Excluir este case de sucesso?')) return;
    try {
      await axios.delete(`${API}/success-cases/${id}`, getAuthHeaders());
      toast.success('Case excluído');
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao excluir case');
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-bold text-gray-800">Cases de Sucesso ({successCases.length})</h2>
          <p className="text-sm text-gray-500">Esses cases aparecem na última tela da landing do parceiro.</p>
        </div>
        <Button
          onClick={() => { resetForm(); setShowForm((current) => !current); }}
          className="bg-orange-600 hover:bg-orange-700 text-white"
        >
          <Plus className="w-4 h-4 mr-2" />
          {showForm && !editingCase ? 'Cancelar' : 'Novo Case'}
        </Button>
      </div>

      {showForm && (
        <Card className="p-6 border-orange-200 bg-orange-50/30">
          <h3 className="font-bold text-gray-800 mb-4">{editingCase ? 'Editar Case' : 'Novo Case'}</h3>
          <form onSubmit={handleSave} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Título *</label>
                <Input
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  required
                  placeholder="Ex: Supermercado reduzindo a conta"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Subtítulo *</label>
                <Input
                  value={form.subtitle}
                  onChange={(e) => setForm({ ...form, subtitle: e.target.value })}
                  required
                  placeholder="Ex: Mais economia todo mês com solar"
                />
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">Foto do Case *</label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center bg-white">
                <input
                  id="success-case-image-upload"
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="hidden"
                />
                {form.image ? (
                  <div className="space-y-3">
                    <img src={form.image} alt={form.title || 'Preview do case'} className="w-full h-52 object-cover rounded-lg" />
                    <div className="flex gap-3">
                      <Button type="button" variant="outline" className="flex-1" onClick={() => setForm((current) => ({ ...current, image: '' }))}>
                        Remover Imagem
                      </Button>
                      <label htmlFor="success-case-image-upload" className="flex-1">
                        <span className="inline-flex w-full items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium shadow-sm cursor-pointer hover:bg-accent hover:text-accent-foreground">
                          Trocar Imagem
                        </span>
                      </label>
                    </div>
                  </div>
                ) : (
                  <label htmlFor="success-case-image-upload" className="block cursor-pointer">
                    <Upload className="w-10 h-10 text-gray-400 mx-auto mb-2" />
                    <p className="text-gray-700 font-medium">Clique para enviar a foto</p>
                    <p className="text-sm text-gray-400 mt-1">Use uma imagem horizontal para melhor resultado no carousel.</p>
                  </label>
                )}
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <Button type="button" onClick={resetForm} variant="outline" className="flex-1">Cancelar</Button>
              <Button type="submit" disabled={saving} className="flex-1 bg-orange-600 hover:bg-orange-700 text-white">
                {saving ? 'Salvando...' : editingCase ? 'Salvar Alterações' : 'Criar Case'}
              </Button>
            </div>
          </form>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {successCases.length === 0 && !showForm && (
          <Card className="col-span-full p-12 text-center">
            <ImageIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">Nenhum case de sucesso cadastrado ainda.</p>
            <p className="text-gray-400 text-sm mt-1">Cadastre fotos, títulos e subtítulos para exibir na landing.</p>
          </Card>
        )}

        {successCases.map((successCase) => (
          <Card key={successCase.id} className="overflow-hidden">
            <img src={successCase.image} alt={successCase.title} className="w-full h-48 object-cover" />
            <div className="p-5">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="font-bold text-gray-800">{successCase.title}</h3>
                  <p className="text-sm text-gray-500 mt-1">{successCase.subtitle}</p>
                </div>
                <div className="flex gap-1.5 shrink-0">
                  <button
                    onClick={() => openEdit(successCase)}
                    className="p-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                  >
                    <Edit className="w-4 h-4 text-gray-600" />
                  </button>
                  <button
                    onClick={() => handleDelete(successCase.id)}
                    className="p-2 bg-red-50 hover:bg-red-100 rounded-lg transition-colors"
                  >
                    <Trash2 className="w-4 h-4 text-red-600" />
                  </button>
                </div>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default SuccessCasesTab;
